import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, NumberInput, Stack, Textarea, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type Reauth, ReauthField, useReauth } from '../../approval-policy.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import work from './PmsWork.module.css';
import { numberFormat } from './plan-view.js';
import type { PlanItem, PostponementInput } from './types.js';

export interface PostponeValues {
  newDueOn: string;
  newDueHours: number | '';
  reason: string;
  classReference: string;
}

/**
 * Erteleme formunun şeması (K10) [NR496 4.5.3d]: yeni vade güncel vadeden sonra; tetikleyici
 * işin vadesiyle aynı (takvim ve/veya saat); gerekçe zorunlu; klas kaleminde klas referansı
 * zorunlu. Parola formun dışındadır (`useReauth`; ADR 0075). Sunucu aynı kuralları `postpone()` ile yeniden denetler.
 */
export function postponeSchema(plan: Pick<PlanItem, 'due'>, classItem: boolean) {
  const currentDate = plan.due.calendarDueDate ?? null;
  const currentHours = plan.due.dueHours ?? null;
  return z
    .object({
      newDueOn: z.string(),
      newDueHours: z.union([z.number(), z.literal('')]),
      reason: z.string(),
      classReference: z.string(),
    })
    .superRefine((v, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', message, path: [path] });
      if (v.newDueOn === '' && v.newDueHours === '') issue('newDueOn', 'NEW_DUE_REQUIRED');
      if (currentDate !== null && v.newDueOn !== '' && v.newDueOn <= currentDate) {
        issue('newDueOn', 'POSTPONEMENT_MUST_BE_LATER');
      }
      if (currentHours !== null && v.newDueHours !== '' && v.newDueHours <= currentHours) {
        issue('newDueHours', 'POSTPONEMENT_MUST_BE_LATER');
      }
      if (v.reason.trim() === '') issue('reason', 'REQUIRED');
      if (classItem && v.classReference.trim() === '')
        issue('classReference', 'CLASS_REFERENCE_REQUIRED');
    });
}

export function toPostponement(
  plan: Pick<PlanItem, 'jobId'>,
  values: PostponeValues,
  workOrderId: string | null = null,
): PostponementInput {
  return {
    jobId: plan.jobId,
    workOrderId,
    newDueOn: values.newDueOn === '' ? null : values.newDueOn,
    newDueHours: values.newDueHours === '' ? null : values.newDueHours,
    reason: values.reason.trim(),
    classReference: values.classReference.trim() === '' ? null : values.classReference.trim(),
  };
}

const EMPTY: PostponeValues = {
  newDueOn: '',
  newDueHours: '',
  reason: '',
  classReference: '',
};

/**
 * Erteleme penceresi (gemide başmühendis; K10): güncel ve ilk orijinal vade görünür; yeni vade ya
 * da saat, gerekçe, klas referansı ve açık onay adımı (parola yalnız şirket ayarı açıkken; ADR
 * 0075). Talep kaptan ve süperintendent onayına gider; vade
 * yalnız onaylı ertelemeyle değişir [NR496 4.5.3d].
 */
export function PostponeDialog({
  opened,
  plan,
  classItem,
  workOrderId = null,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  plan: PlanItem | null;
  classItem: boolean;
  workOrderId?: string | null;
  onClose: () => void;
  onSubmit: (input: PostponementInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const reauth = useReauth('postponement_request');
  const resetPassword = reauth.reset;
  const form = useForm<PostponeValues>({
    resolver: zodResolver(postponeSchema(plan ?? { due: {} }, classItem)),
    defaultValues: EMPTY,
  });
  useEffect(() => {
    if (opened) {
      form.reset(EMPTY);
      resetPassword();
      setError(null);
    }
  }, [opened, form, resetPassword]);
  if (!plan) return null;
  const submit = form.handleSubmit(async (values) => {
    if (reauth.missing) return;
    setError(null);
    try {
      await onSubmit({ ...toPostponement(plan, values, workOrderId), ...reauth.body() });
    } catch (failure) {
      reauth.noteError(failure);
      setError(failure);
    }
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('pms.work.postpone.title', { code: plan.code })}
      closeButtonProps={{ 'aria-label': t('pms.work.password.back') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <PostponeFields form={form} plan={plan} classItem={classItem} reauth={reauth} />
        {error ? (
          <p className={work.error} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('pms.work.password.back')}
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting} disabled={reauth.missing}>
            {t('pms.work.postpone.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Pencerenin alanları (bileşen testi sunucu çizimiyle). */
export function PostponeFields({
  form,
  plan,
  classItem,
  reauth,
}: {
  form: UseFormReturn<PostponeValues>;
  plan: PlanItem;
  classItem: boolean;
  reauth: Reauth;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.postpone.${key}`, values);
  const error = (name: keyof PostponeValues) => {
    const message = form.formState.errors[name]?.message;
    return message
      ? t(`pms.fieldErrors.${message}`, {
          defaultValue: t(`pms.errors.${message}`, {
            defaultValue: t('pms.errors.VALIDATION_FAILED'),
          }),
        })
      : undefined;
  };
  const language = i18n.resolvedLanguage ?? 'en';
  const hours = (value: number | undefined) =>
    value === undefined ? '—' : t('pms.hours', { value: numberFormat(language, value) });
  return (
    <Stack gap="sm">
      <dl className={work.facts}>
        <div>
          <dt>{k('job')}</dt>
          <dd>
            {plan.code} · {plan.title}
          </dd>
        </div>
        <div>
          <dt>{k('component')}</dt>
          <dd>{plan.equipmentName ?? '—'}</dd>
        </div>
        <div>
          <dt>{k('currentDue')}</dt>
          <dd>
            {formatDay(plan.due.calendarDueDate ?? plan.due.dueOn)}
            {plan.due.dueHours !== undefined ? ` · ${hours(plan.due.dueHours)}` : ''}
          </dd>
        </div>
        <div>
          <dt>{k('firstOriginalDue')}</dt>
          <dd>
            {formatDay(plan.firstOriginalDue.calendarDueDate ?? plan.firstOriginalDue.dueOn)}
            {plan.firstOriginalDue.dueHours !== undefined
              ? ` · ${hours(plan.firstOriginalDue.dueHours)}`
              : ''}
          </dd>
        </div>
      </dl>
      <p className={work.note}>{k('hint')}</p>
      {plan.due.calendarDueDate !== undefined ? (
        <Controller
          control={form.control}
          name="newDueOn"
          render={({ field }) => (
            <DayInput
              label={k('newDueOn')}
              value={field.value || null}
              onChange={(value) => field.onChange(value ?? '')}
              minDate={plan.due.calendarDueDate}
              error={error('newDueOn')}
            />
          )}
        />
      ) : null}
      {plan.due.dueHours !== undefined ? (
        <Controller
          control={form.control}
          name="newDueHours"
          render={({ field }) => (
            <NumberInput
              label={k('newDueHours')}
              min={0}
              decimalScale={1}
              value={field.value}
              onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
              error={error('newDueHours')}
            />
          )}
        />
      ) : null}
      <Textarea
        label={k('reason')}
        required
        autosize
        minRows={2}
        {...form.register('reason')}
        error={error('reason')}
      />
      <TextInput
        label={k('classReference')}
        description={classItem ? k('classReferenceRequired') : k('classReferenceHint')}
        required={classItem}
        {...form.register('classReference')}
        error={error('classReference')}
      />
      <ReauthField
        reauth={reauth}
        label={t('pms.work.password.password')}
        description={t('pms.work.password.hint')}
        mb="0"
      />
    </Stack>
  );
}
